/**
 * Axios interceptors for MuseFlow API authentication.
 *
 * Request: attaches `Authorization: Bearer <token>` to every MuseFlow API
 * request via authService.getToken(). The header is set authoritatively here —
 * manual headers at call sites are overridden, so a stale or missing token can
 * never be sent (the "Bearer undefined" class of bug, MUS-264).
 *
 * Response: on 401, refreshes the token once (single-flight inside
 * AuthService) and retries the request. A 401 carrying invalid_grant means
 * the session is dead and is reported to AuthService, which tears down both
 * identity stacks and notifies the React layer.
 *
 * Also on response: a 403 carrying the Layer-2 entitlement gate's
 * "Active subscription required" marker means the signed-in user is not a
 * paying subscriber (MUS-532). We surface the existing paywall via
 * notifyEntitlementRequired() instead of a generic error / blank / crash. This
 * is checked before the 401 path and before generic error reporting; it is
 * keyed on the gate's marker (see entitlementGate.isEntitlementDenied), so an
 * open-resource 403, an ordinary authorization 403, or a 401 can never be
 * mistaken for an entitlement denial.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { authService } from './AuthService';
import logger from './Logger';
import { reportError } from './reportError';
import { isEntitlementDenied, notifyEntitlementRequired } from './entitlementGate';

const apiOrigin = process.env.REACT_APP_BACKEND_URL ?? '';

function isMuseflowApiRequest(url: string | undefined): boolean {
  if (!url) return false;
  return (apiOrigin !== '' && url.startsWith(apiOrigin)) || url.startsWith('/api/');
}

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Sets up axios interceptors for auth header injection and 401 handling.
 * Call this once during app initialization.
 */
export function setupAxiosInterceptors() {
  axios.interceptors.request.use(async (config) => {
    if (!isMuseflowApiRequest(config.url)) {
      return config;
    }
    let token: string | undefined;
    try {
      token = await authService.getToken();
    } catch (error) {
      // getToken should never throw (it resolves to undefined on failure), but
      // if it does, keep the request flowing token-less rather than breaking it.
      reportError(error, 'http.getToken');
      token = undefined;
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Never send "Bearer undefined" — an absent header fails faster and
      // honestly at the API, and skips cargo-cult auth on public endpoints.
      delete config.headers.Authorization;
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // MUS-651: a request the app cancelled itself (a phrase try the lesson
      // stopped: replaced by Retry, no longer needed once the phrase was served,
      // or an extra try left behind by a session change or by leaving) is not
      // an error.
      if (axios.isCancel(error)) return Promise.reject(error);

      const originalRequest = error.config as RetryableRequest | undefined;

      // Entitlement gate (Layer 2, enforce mode): a 403 carrying the gate's
      // "Active subscription required" marker means the signed-in user is not a
      // paying subscriber. Show the existing paywall rather than surfacing an
      // error / blank / crash (MUS-532). Keyed on the marker, so an open-resource
      // 403, an ordinary 403, or a 401 is never swallowed into the paywall.
      //
      // We still reject: no caller should proceed as if it received data. The
      // full-screen, non-exitable paywall covers whatever partial/blank state the
      // rejected fetch leaves behind, and the entitlement denial is expected
      // under enforce — so it is not reported as an error here.
      if (isMuseflowApiRequest(originalRequest?.url) && isEntitlementDenied(error)) {
        logger.info('[Axios] 403 entitlement denial — surfacing paywall (MUS-532)');
        notifyEntitlementRequired();
        return Promise.reject(error);
      }

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        !isMuseflowApiRequest(originalRequest.url)
      ) {
        reportError(error, 'http.response');
        return Promise.reject(error);
      }

      if (originalRequest._retry) {
        logger.warn('[Axios] Request already retried after refresh, giving up');
        reportError(error, 'http.retryExhausted');
        return Promise.reject(error);
      }

      const responseData = error.response?.data as { error?: string } | undefined;
      if (responseData?.error === 'invalid_grant') {
        logger.error('[Axios] API returned 401 with invalid_grant — session is dead');
        reportError(error, 'http.deadSession');
        await authService.reportSessionInvalid();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      logger.info('[Axios] 401 received, attempting token refresh');

      // Refresh is single-flight inside AuthService; concurrent 401s share it.
      // getToken() also handles the dead-session path (invalid_grant on
      // refresh), so a missing token here just means the retry cannot succeed.
      const freshToken = await authService.getToken();
      if (!freshToken) {
        logger.warn('[Axios] No token available after refresh attempt');
        reportError(error, 'http.retryFailed');
        return Promise.reject(error);
      }

      originalRequest.headers.Authorization = `Bearer ${freshToken}`;
      return axios(originalRequest);
    },
  );

  logger.info('[Axios] Auth interceptors configured');
}
